// Basic
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
import { useTranslation } from 'react-i18next';
// Components
import Icon from '../icon/Icon';
import TooltipHelper from '../tooltip/TooltipHelper';
// Common
import createDataCyAttribute from '@/common-utils/functions/createDataCyAttribute';
// Style
import './Entity.scss';

/**
 * Entity Component
 *
 * @param props - Props object
 * @param props.entity - Key granting access to the text to display
 * @param props.namespace - JSON name where to find traduction (if the name is 'common' there is no need to fill this)
 * @param props.data - Data informations added to traduction
 * @param props.nowrap
 * @param props.className
 * @param props.icon
 * @param props.title
 * @param props.iconClassName
 * @param props.iconSize
 * @param props.alternativeDataCy
 * @returns Span element with translated text in it
 */
function Entity({ entity, namespace, data, nowrap, className, icon, iconClassName, iconSize, title, alternativeDataCy, ...props }) {
    // Translation
    const { t: translate } = useTranslation(namespace || undefined);

    if (!entity) {
        return null;
    }

    className = nowrap ? `nowrap ${className}` : className;

    // !! Use alternativeDataCy only when needed, otherwise a data-cy attribute based on the entity is created
    const dataCyAttribute = alternativeDataCy ?? createDataCyAttribute(entity);

    const translatedEntity = <span
        className={cn(icon && 'flex items-center', className)}
        data-cy={dataCyAttribute}
        {...props}
    >
        {icon && <Icon icon={icon} className={cn('left-icon', iconClassName)} size={iconSize} />}
        {translate(entity, data)}
    </span>;

    return (
        <>
            {
                title ?
                    <TooltipHelper
                        asChild
                        content={translatedEntity}
                        trigger={translatedEntity}
                    />
                    :
                    translatedEntity
            }
        </>
    );
}

Entity.propTypes = {
    entity: PropTypes.string,
    namespace: PropTypes.string,
    data: PropTypes.object,
    props: PropTypes.any,
    nowrap: PropTypes.bool,
    className: PropTypes.string,
    title: PropTypes.bool,
    icon: PropTypes.string,
    iconClassName: PropTypes.string,
    iconSize: PropTypes.number,
    alternativeDataCy: PropTypes.string
};

export default Entity;